import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import type { NotificationDTO } from '@siliconx/shared';
import { cn } from '@siliconx/shared/ui/utils';
import { QueryBoundary } from '@siliconx/shared/ui/QueryBoundary';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { api, throwIfNotOk } from '../lib/api';
import { isNotificationLinkable, relativeTime, renderNotification } from '../lib/notifications';
import { qk } from '../lib/queryKeys';

export const Route = createFileRoute('/notifications')({
  component: NotificationsPage,
});

interface ListResponse {
  data: NotificationDTO[];
  meta: { nextCursor: string | null; limit: number };
}

// Full /notifications page (SIL-160) — same read endpoint as the bell
// dropdown, cursor-paged (prev/next, mirrors apps/customer/src/routes/assets/index.tsx).
function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [cursor, setCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const notificationsQ = useQuery<ListResponse>({
    queryKey: qk.notifications.page(cursor),
    queryFn: async () => {
      const res = await api.api.client.notifications.$get({
        query: { limit: '15', ...(cursor ? { cursor } : {}) },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      return (await res.json()) as unknown as ListResponse;
    },
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.notifications['read-all'].$post();
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.notifications.all });
    },
  });

  const markOneRead = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.api.client.notifications[':id'].read.$post({ param: { id } });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.notifications.all });
    },
  });

  function nextPage() {
    if (notificationsQ.data?.meta.nextCursor) {
      setCursorStack((s) => [...s, cursor ?? '']);
      setCursor(notificationsQ.data.meta.nextCursor);
    }
  }

  function prevPage() {
    const stack = [...cursorStack];
    const prev = stack.pop() ?? null;
    setCursorStack(stack);
    setCursor(prev);
  }

  function handleRowClick(n: NotificationDTO) {
    if (!n.read) markOneRead.mutate(n.id);
    if (isNotificationLinkable(n.type)) {
      void navigate({ to: n.linkPath });
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        {/* Notifications is a self-evident list page (Earned-Subtitle Rule) — no subtitle. */}
        <PageHeader title="Notifications" />
        <button
          type="button"
          onClick={() => markAllRead.mutate()}
          disabled={markAllRead.isPending}
          className="flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:bg-muted disabled:opacity-40"
        >
          <Check className="size-3.5" /> Mark all read
        </button>
      </div>

      <QueryBoundary
        query={notificationsQ}
        errorTitle="Couldn't load notifications."
        empty={(d) => d.data.length === 0}
        emptyState={<p className="rounded-lg border border-border bg-card px-4 py-8 text-center text-sm text-muted-foreground">No notifications yet.</p>}
        loadingRows={3}
      >
        {(data) => (
          <>
            <div className="rounded-lg border border-border bg-card">
              {data.data.map((n) => {
                const { title, detail } = renderNotification(n);
                const linkable = isNotificationLinkable(n.type);
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => handleRowClick(n)}
                    disabled={!linkable && n.read}
                    className={cn(
                      'flex w-full items-start gap-3 border-b border-border px-4 py-3 text-left last:border-b-0 hover:bg-muted',
                      !n.read && 'bg-brand-50/60',
                      !linkable && 'cursor-default hover:bg-transparent',
                    )}
                  >
                    {!n.read ? (
                      <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-brand-600" />
                    ) : (
                      <span className="mt-1.5 size-1.5 shrink-0" />
                    )}
                    <div className="flex-1">
                      <p className={cn('text-sm', n.read ? 'text-muted-foreground' : 'font-medium text-foreground')}>
                        {title}
                      </p>
                      {detail && <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p>}
                    </div>
                    <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
                      {relativeTime(n.createdAt)}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                disabled={cursorStack.length === 0}
                onClick={prevPage}
                className="rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground disabled:opacity-40 hover:bg-muted"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={!data.meta.nextCursor}
                onClick={nextPage}
                className="rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground disabled:opacity-40 hover:bg-muted"
              >
                Next
              </button>
            </div>
          </>
        )}
      </QueryBoundary>
    </div>
  );
}
